import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { isMobileViewport } from "../hooks/useIsMobileViewport";
import { parseTwitchChatEmbedChannel } from "../services/twitchServices";
import { LivestreamPopoutProvider, useLivestreamPopout } from "./LivestreamPopoutProvider";

interface LivestreamChatConfig {
  src: string;
  title: string;
}

interface LivestreamChatContextValue {
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  toggleChat: () => void;
  setChatConfig: (config: LivestreamChatConfig | null) => void;
  registerChatSlot: (element: HTMLDivElement | null) => void;
  registerPopoutChatSlot: (element: HTMLDivElement | null) => void;
}

const LivestreamChatContext = createContext<LivestreamChatContextValue | null>(null);

const LivestreamChatPortal = ({
  chatConfig,
  chatOpen,
  inlineSlot,
  popoutSlot,
  parkingNode,
}: {
  chatConfig: LivestreamChatConfig | null;
  chatOpen: boolean;
  inlineSlot: HTMLDivElement | null;
  popoutSlot: HTMLDivElement | null;
  parkingNode: HTMLDivElement | null;
}) => {
  const { isPoppedOut } = useLivestreamPopout();

  const isInPopoutSlot = chatOpen && isPoppedOut && Boolean(popoutSlot);
  const isInInlineSlot = chatOpen && !isPoppedOut && Boolean(inlineSlot);
  const portalTarget = isInPopoutSlot ? popoutSlot : isInInlineSlot ? inlineSlot : parkingNode;

  if (!chatConfig || !portalTarget) {
    return null;
  }

  return createPortal(
    <div
      className={
        isInPopoutSlot
          ? "flex-1 min-w-0 min-h-0 overflow-hidden bg-[#0e0e10]"
          : "flex-1 min-w-0 min-h-0 w-full rounded-[0.75rem] overflow-hidden border border-[rgba(44,137,225,0.35)] bg-[#0e0e10]"
      }
    >
      <iframe
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        src={chatConfig.src}
        title={chatConfig.title}
        className="block w-full h-full min-h-full border-none"
      />
    </div>,
    portalTarget,
  );
};

export const LivestreamChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [parkingNode, setParkingNode] = useState<HTMLDivElement | null>(null);
  const [chatOpen, setChatOpen] = useState(() => !isMobileViewport());
  const [chatConfig, setChatConfigState] = useState<LivestreamChatConfig | null>(null);
  const [inlineSlot, setInlineSlot] = useState<HTMLDivElement | null>(null);
  const [popoutSlot, setPopoutSlot] = useState<HTMLDivElement | null>(null);

  const registerChatSlot = useCallback((element: HTMLDivElement | null) => {
    setInlineSlot(element);
  }, []);

  const registerPopoutChatSlot = useCallback((element: HTMLDivElement | null) => {
    setPopoutSlot(element);
  }, []);

  const setChatConfig = useCallback((config: LivestreamChatConfig | null) => {
    setChatConfigState((current) => {
      if (!config) {
        return null;
      }

      if (current) {
        const currentChannel = parseTwitchChatEmbedChannel(current.src);
        const nextChannel = parseTwitchChatEmbedChannel(config.src);
        if (
          currentChannel !== "" &&
          currentChannel === nextChannel &&
          current.title === config.title
        ) {
          return current;
        }
        if (currentChannel !== "" && currentChannel === nextChannel) {
          return { src: current.src, title: config.title };
        }
      }

      if (current?.src === config.src && current.title === config.title) {
        return current;
      }
      return config;
    });
  }, []);

  const toggleChat = useCallback(() => {
    setChatOpen((open) => !open);
  }, []);

  const value = useMemo(
    () => ({
      chatOpen,
      setChatOpen,
      toggleChat,
      setChatConfig,
      registerChatSlot,
      registerPopoutChatSlot,
    }),
    [chatOpen, setChatConfig, registerChatSlot, registerPopoutChatSlot, toggleChat],
  );

  return (
    <LivestreamChatContext.Provider value={value}>
      <div
        ref={setParkingNode}
        className="fixed left-[-10000px] top-0 w-[20rem] h-[min(42vh,22rem)] overflow-hidden invisible pointer-events-none"
        aria-hidden
      />
      <LivestreamPopoutProvider>
        <LivestreamChatPortal
          chatConfig={chatConfig}
          chatOpen={chatOpen}
          inlineSlot={inlineSlot}
          popoutSlot={popoutSlot}
          parkingNode={parkingNode}
        />
        {children}
      </LivestreamPopoutProvider>
    </LivestreamChatContext.Provider>
  );
};

export const useLivestreamChat = () => {
  const context = useContext(LivestreamChatContext);
  if (!context) {
    throw new Error("useLivestreamChat must be used within LivestreamChatProvider");
  }
  return context;
};
